import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, Mail, MessageCircle, Phone, ExternalLink } from 'lucide-react'

const FAQ_SECTIONS = [
  {
    title: 'Getting Started',
    questions: [
      {
        q: 'How do I start using Drive Without Fear?',
        a: 'After signing up, you\'ll complete a brief onboarding questionnaire that helps us understand your needs. Then you can start with our free trial videos, which introduce you to our approach. If you find the program helpful, you can upgrade to full access.',
      },
      {
        q: 'What happens during onboarding?',
        a: 'During onboarding, we\'ll ask you some questions about your driving anxiety experience. This helps us personalize your therapy journey. The process takes about 5-10 minutes and your responses are kept completely confidential.',
      },
      {
        q: 'Is my information kept private?',
        a: 'Absolutely. We take privacy very seriously. Your data is stored securely in the EU, encrypted at rest and in transit, and never shared with third parties for marketing. Only you and any support person you choose to link with can access your progress data.',
      },
    ],
  },
  {
    title: 'Using the Program',
    questions: [
      {
        q: 'How do the anxiety ratings work?',
        a: 'After watching each video, you\'ll rate your anxiety on a scale of 0-8. This helps track your progress and determine when you\'re ready to move to more challenging content. Ratings of 0 mean you didn\'t experience any anxiety, while ratings of 6-8 indicate high anxiety.',
      },
      {
        q: 'What if I feel too anxious watching a video?',
        a: 'That\'s completely normal. If you rate your anxiety as 4 or higher, we\'ll suggest rewatching the current video before moving on. There\'s no rush - healing takes time, and everyone progresses at their own pace.',
      },
      {
        q: 'What are voice memos for?',
        a: 'Voice memos (available with full access) let you record your thoughts, feelings, and experiences throughout your therapy journey. You can choose to share these with your linked support person for more personalized support.',
      },
      {
        q: 'How do I link with a support person?',
        a: 'A support person can be a clinician, GP, family member, friend, or any other trusted person. Your support person will provide you with an 8-digit code. Go to your support person settings and enter this code to establish the link. Once linked, your support person can view your progress and assign personalized video content.',
      },
    ],
  },
  {
    title: 'Payment & Subscription',
    questions: [
      {
        q: 'How much does the full program cost?',
        a: 'The full program is a one-time payment of EUR 250. This gives you lifetime access to all videos, voice memo features, support person linking, and all future content updates.',
      },
      {
        q: 'Is there a refund policy?',
        a: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied with the program for any reason, contact us within 30 days of purchase for a full refund.',
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit and debit cards through our secure payment processor, Stripe. Your payment details are never stored on our servers.',
      },
    ],
  },
  {
    title: 'Technical Support',
    questions: [
      {
        q: 'Videos aren\'t playing. What should I do?',
        a: 'First, check your internet connection. Then try refreshing the page or clearing your browser cache. Make sure you\'re using a modern browser (Chrome, Firefox, Safari, or Edge). If problems persist, contact support.',
      },
      {
        q: 'I forgot my password. How do I reset it?',
        a: 'Click "Forgot password" on the log in page and enter your email address. We\'ll send you a link to reset your password. Check your spam folder if you don\'t see the email.',
      },
      {
        q: 'Can I use the app on multiple devices?',
        a: 'Yes, your account works on any device with a web browser. Your progress syncs automatically across all devices.',
      },
    ],
  },
]

const CRISIS_RESOURCES = [
  {
    name: 'Samaritans Ireland',
    phone: '116 123',
    description: '24-hour emotional support',
  },
  {
    name: 'Pieta House',
    phone: '1800 247 247',
    description: 'Crisis support and suicide prevention',
  },
  {
    name: 'Text 50808',
    phone: 'Text HELLO to 50808',
    description: '24/7 text-based crisis support',
  },
]

export function HelpPage() {
  const navigate = useNavigate()
  const [openSection, setOpenSection] = useState<string | null>('Getting Started')
  const [openQuestion, setOpenQuestion] = useState<string | null>(null)

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      {/* Header */}
      <div className="bg-white border-b border-[#E3DED2]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#6E7582] hover:text-[#0F1C2E]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#D4AF5A] to-[#C09A4E] text-white">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Help Center</h1>
          <p className="text-white/80 text-lg">
            Find answers to common questions or reach out to our support team
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-[#0F1C2E] mb-8">Frequently Asked Questions</h2>

        <div className="space-y-4">
          {FAQ_SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <button
                onClick={() => setOpenSection(openSection === section.title ? null : section.title)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <span className="font-semibold text-[#0F1C2E]">{section.title}</span>
                <ChevronDown
                  className={`w-5 h-5 text-[#A4A9B3] transition-transform ${
                    openSection === section.title ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {openSection === section.title && (
                <div className="px-6 pb-4 space-y-2">
                  {section.questions.map((item) => (
                    <div key={item.q} className="border-t border-[#E3DED2]">
                      <button
                        onClick={() => setOpenQuestion(openQuestion === item.q ? null : item.q)}
                        className="w-full py-4 flex items-start justify-between text-left"
                      >
                        <span className="text-[#0F1C2E] pr-4">{item.q}</span>
                        <ChevronDown
                          className={`w-4 h-4 text-[#A4A9B3] flex-shrink-0 mt-1 transition-transform ${
                            openQuestion === item.q ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openQuestion === item.q && (
                        <p className="text-[#6E7582] pb-4 text-sm leading-relaxed">{item.a}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-white border-t border-[#E3DED2]">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-[#0F1C2E] mb-8">Contact Support</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#4A6484]/10 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#4A6484]/15 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-[#4A6484]" />
              </div>
              <h3 className="font-semibold text-[#0F1C2E] mb-2">Email Support</h3>
              <p className="text-[#6E7582] text-sm mb-4">
                For general inquiries and non-urgent support
              </p>
              <a
                href="mailto:support@drivewithoutfear.com"
                className="text-[#4A6484] font-medium hover:underline"
              >
                support@drivewithoutfear.com
              </a>
            </div>

            <div className="bg-[#6B9D7A]/8 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6B9D7A]/15 rounded-xl flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6 text-[#6B9D7A]" />
              </div>
              <h3 className="font-semibold text-[#0F1C2E] mb-2">Response Time</h3>
              <p className="text-[#6E7582] text-sm">
                We aim to respond to all inquiries within 24-48 hours during business days.
                For urgent matters, please include "URGENT" in your subject line.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Crisis Resources */}
      <div className="bg-[#C7796B]/8 border-t border-[#C7796B]/25">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-[#C7796B] mb-4">Need Immediate Help?</h2>
          <p className="text-[#C7796B] mb-8">
            If you're in crisis or experiencing thoughts of self-harm, please reach out to
            professional crisis support services immediately.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {CRISIS_RESOURCES.map((resource) => (
              <div key={resource.name} className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold text-[#0F1C2E] mb-1">{resource.name}</h3>
                <p className="text-sm text-[#6E7582] mb-2">{resource.description}</p>
                <div className="flex items-center gap-2 text-[#C7796B] font-medium">
                  <Phone className="w-4 h-4" />
                  {resource.phone}
                </div>
              </div>
            ))}
          </div>

          <p className="text-sm text-[#C7796B] mt-6">
            <ExternalLink className="w-4 h-4 inline mr-1" />
            In an emergency, always call 112 or 999
          </p>
        </div>
      </div>
    </div>
  )
}
